import { useState, useEffect } from 'react'
import {
  getMusicPlaylists, addMusicPlaylistStart, deleteMusicPlaylist,
  getMusic, getMusicTaste, analyzeMusicStart, musicCompatibilityStart, musicCrossRecommendStart,
  getParticipantsList, pollThreadsJob,
} from '../utils/api'
import { getModelPref } from '../utils/modelPrefs'
import ModelToggle from './ModelToggle'
import { C, font } from './ProfileResult'

function useIsMobile(bp = 640) {
  const [v, setV] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    let t; const h = () => { clearTimeout(t); t = setTimeout(() => setV(window.innerWidth < bp), 80) }
    window.addEventListener('resize', h); return () => { clearTimeout(t); window.removeEventListener('resize', h) }
  }, [bp]); return v
}

const glass = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: '20px 24px',
  marginBottom: 16,
}

const sectionLabel = {
  fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase',
  color: C.textDim, marginBottom: 10,
}

const btnPrimary = {
  padding: '8px 18px', borderRadius: 8, fontFamily: font.body, fontSize: 12,
  background: 'rgba(184,189,212,0.12)', border: `1px solid ${C.borderHi}`,
  color: C.accent, cursor: 'pointer',
}

const btnGhost = {
  padding: '8px 18px', borderRadius: 8, fontFamily: font.body, fontSize: 12,
  background: 'rgba(184,189,212,0.08)', border: `1px solid ${C.border}`,
  color: C.textMuted, cursor: 'pointer',
}

const selectStyle = {
  padding: '8px 12px', borderRadius: 8, fontFamily: font.body, fontSize: 12,
  background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, color: C.text,
}

function ScoreBar({ score }) {
  const color = score >= 75 ? 'rgba(77,200,138,0.9)' : score >= 45 ? C.accent : 'rgba(220,140,60,0.9)'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <div style={{ flex: 1, height: 4, background: C.border, borderRadius: 2, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
      </div>
      <span style={{ fontFamily: font.mono, fontSize: 12, color, minWidth: 34 }}>{score}%</span>
    </div>
  )
}

// Single-hue horizontal bar for ranked magnitude data (genres, artists) — one series,
// so no categorical color coding needed; the label + bar length carry the identity/magnitude.
// `value`/`maxValue` drive the bar's fill width (any magnitude — a % or a raw count both work
// the same way here since it's relative to the top row). `display` is the exact right-side
// label text — kept separate from `value` so a "12×" count is never mislabeled as a percent.
function RankBar({ label, value, maxValue, display }) {
  const width = maxValue > 0 ? Math.max((value / maxValue) * 100, 3) : 0
  return (
    <div style={{ marginBottom: 9 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 3 }}>
        <span style={{ fontFamily: font.body, fontSize: 12, color: C.text, textTransform: 'capitalize' }}>{label}</span>
        <span style={{ fontFamily: font.mono, fontSize: 10.5, color: C.textDim, flexShrink: 0 }}>{display}</span>
      </div>
      <div style={{ height: 6, background: 'rgba(255,255,255,0.04)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${width}%`, background: C.accent, opacity: 0.75, borderRadius: 3, transition: 'width 0.5s ease' }} />
      </div>
    </div>
  )
}

function formatDuration(ms) {
  if (!ms) return null
  const totalSec = Math.round(ms / 1000)
  const min = Math.floor(totalSec / 60)
  const sec = totalSec % 60
  return `${min}:${String(sec).padStart(2, '0')}`
}

function MatchBadge({ match, note }) {
  if (typeof match !== 'boolean') return null
  const color = match ? 'rgba(77,200,138,0.85)' : 'rgba(220,140,60,0.85)'
  const bg = match ? 'rgba(77,200,138,0.08)' : 'rgba(220,140,60,0.08)'
  const border = match ? 'rgba(77,200,138,0.25)' : 'rgba(220,140,60,0.25)'
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6,
      padding: '2px 8px', borderRadius: 5, background: bg, border: `1px solid ${border}`,
    }}>
      <span style={{ fontFamily: font.mono, fontSize: 10, color }}>{match ? '✓' : '△'}</span>
      {note && <span style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted }}>{note}</span>}
    </div>
  )
}

function PlaylistCard({ pl, onDelete, isOwner }) {
  return (
    <div style={{
      padding: '12px 16px', borderRadius: 8, marginBottom: 10,
      background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{
              fontFamily: font.mono, fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase',
              padding: '1px 7px', borderRadius: 4, color: C.textDim, border: `1px solid ${C.border}`,
            }}>
              {pl.source === 'spotify' ? 'Spotify' : 'Яндекс.Музыка'}
            </span>
            <span style={{ fontFamily: font.body, fontSize: 13, color: C.text }}>{pl.title}</span>
          </div>
          <div style={{ fontFamily: font.mono, fontSize: 10.5, color: C.textDim }}>
            {pl.track_count > 5000 ? `5000 из ${pl.track_count} треков (первые)` : `${pl.track_count} треков`}
          </div>
        </div>
        {isOwner && (
          <button
            onClick={() => onDelete(pl.id)}
            title="Удалить только из базы двойника — на Spotify/Яндекс.Музыку не влияет"
            style={{ background: 'none', border: 'none', color: C.textDim, cursor: 'pointer', fontFamily: font.mono, fontSize: 13 }}
          >✕</button>
        )}
      </div>
      {pl.source === 'spotify' && (
        <iframe
          style={{ borderRadius: 10, marginTop: 10 }}
          src={`https://open.spotify.com/embed/playlist/${pl.url.match(/playlist\/([a-zA-Z0-9]+)/)?.[1] || ''}`}
          width="100%" height="152" frameBorder="0" loading="lazy"
          allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
        />
      )}
      {pl.source === 'yandex' && (
        <a href={pl.url} target="_blank" rel="noreferrer" style={{
          display: 'inline-block', marginTop: 8, fontFamily: font.mono, fontSize: 11, color: C.accent,
        }}>Открыть в Яндекс.Музыке ↗</a>
      )}
    </div>
  )
}

export default function MusicTab({ userId = 'user', isOwner = false, ownerUserId = 'user' }) {
  const isMobile = useIsMobile()

  const [loading, setLoading] = useState(true)
  const [playlists, setPlaylists] = useState([])
  const [newUrl, setNewUrl] = useState('')
  const [addingPlaylist, setAddingPlaylist] = useState(false)
  const [error, setError] = useState(null)

  const [musicData, setMusicData] = useState(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [sampleSize, setSampleSize] = useState(300)

  const [taste, setTaste] = useState(null)

  const [otherProfiles, setOtherProfiles] = useState([])

  const [compatTarget, setCompatTarget] = useState('')
  const [compatResult, setCompatResult] = useState(null)
  const [compatLoading, setCompatLoading] = useState(false)
  const [compatError, setCompatError] = useState(null)

  const [crossTarget, setCrossTarget] = useState('')
  const [crossResult, setCrossResult] = useState(null)
  const [crossLoading, setCrossLoading] = useState(false)
  const [crossError, setCrossError] = useState(null)

  const loadPlaylists = () => {
    getMusicPlaylists(userId).then(d => setPlaylists(d.playlists || [])).catch(() => {})
    getMusicTaste(userId).then(d => setTaste(d.empty ? null : d)).catch(() => {})
  }

  useEffect(() => {
    setLoading(true)
    Promise.all([
      getMusicPlaylists(userId).then(d => setPlaylists(d.playlists || [])).catch(() => {}),
      getMusicTaste(userId).then(d => setTaste(d.empty ? null : d)).catch(() => {}),
      getMusic(userId).then(d => { if (!d.empty && !d.error) setMusicData(d) }).catch(() => {}),
      getParticipantsList(ownerUserId).then(list => {
        const options = [{ id: ownerUserId, label: 'Владелец' },
          ...list.map(p => ({ id: `participant_${p.id}`, label: p.display_name }))]
        setOtherProfiles(options.filter(o => o.id !== userId))
      }).catch(() => {}),
    ]).finally(() => setLoading(false))
  }, [userId, ownerUserId])

  const handleAddPlaylist = async () => {
    if (!newUrl.trim()) return
    setAddingPlaylist(true); setError(null)
    try {
      const jobId = await addMusicPlaylistStart(userId, newUrl.trim())
      await pollThreadsJob(jobId, null, 3 * 60 * 1000)
      setNewUrl('')
      loadPlaylists()
    } catch (err) {
      setError(err.message)
    } finally {
      setAddingPlaylist(false)
    }
  }

  const handleDeletePlaylist = async (playlistId) => {
    const ok = window.confirm(
      'Удалить плейлист из профиля двойника?\n\n' +
      'Удаляется только запись в базе двойника. Из вашего аккаунта Spotify или Яндекс.Музыки ' +
      'ничего не удаляется и не изменяется — у приложения нет доступа на запись, только на чтение.'
    )
    if (!ok) return
    try {
      await deleteMusicPlaylist(playlistId)
      loadPlaylists()
    } catch (err) {
      setError(err.message)
    }
  }

  const runAnalyze = async () => {
    setAnalyzing(true); setError(null)
    try {
      const jobId = await analyzeMusicStart(userId, getModelPref('music'), userId, sampleSize)
      const result = await pollThreadsJob(jobId, null, 5 * 60 * 1000)
      setMusicData(result)
    } catch (err) {
      setError(err.message)
    } finally {
      setAnalyzing(false)
    }
  }

  const runCompatibility = async () => {
    if (!compatTarget) return
    setCompatLoading(true); setCompatError(null); setCompatResult(null)
    try {
      const jobId = await musicCompatibilityStart(userId, compatTarget, getModelPref('music'))
      const result = await pollThreadsJob(jobId, null, 3 * 60 * 1000)
      setCompatResult(result)
    } catch (err) {
      setCompatError(err.message)
    } finally {
      setCompatLoading(false)
    }
  }

  const runCrossRecommend = async () => {
    if (!crossTarget) return
    setCrossLoading(true); setCrossError(null); setCrossResult(null)
    try {
      const jobId = await musicCrossRecommendStart(userId, crossTarget, getModelPref('music'))
      const result = await pollThreadsJob(jobId, null, 3 * 60 * 1000)
      setCrossResult(result)
    } catch (err) {
      setCrossError(err.message)
    } finally {
      setCrossLoading(false)
    }
  }

  if (loading) {
    return <div style={{ padding: '60px 0', textAlign: 'center', fontFamily: font.mono, fontSize: 11, color: C.textDim }}>Загрузка...</div>
  }

  return (
    <div style={{ paddingBottom: 60 }}>
      <div style={glass}>
        <div style={sectionLabel}>Музыка</div>
        <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, maxWidth: 560, marginBottom: isOwner ? 16 : 0 }}>
          Плейлисты Spotify и Яндекс.Музыки — по публичной ссылке, без входа в аккаунт. Слепое чтение личности
          по музыке, совместимость вкуса с другими профилями, рекомендации треков. Развлекательный жанр, не наука.
        </p>
        {isOwner && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              value={newUrl}
              onChange={e => setNewUrl(e.target.value)}
              placeholder="Ссылка на плейлист Spotify или Яндекс.Музыки"
              style={{ ...selectStyle, flex: 1, minWidth: 240 }}
            />
            <button onClick={handleAddPlaylist} disabled={addingPlaylist} style={btnPrimary}>
              {addingPlaylist ? '⏳ Добавляю...' : '+ Добавить плейлист'}
            </button>
          </div>
        )}
      </div>

      {error && (
        <div style={{ padding: '10px 16px', borderRadius: 8, background: 'rgba(212,90,90,0.08)', border: '1px solid rgba(212,90,90,0.25)', color: 'rgba(220,100,100,0.9)', fontFamily: font.body, fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {taste && (
        <div style={glass}>
          <div style={sectionLabel}>Профиль вкуса</div>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: font.heading, fontSize: 22, color: C.silverHi, fontWeight: 400 }}>{taste.total_tracks}</div>
              <div style={{ fontFamily: font.mono, fontSize: 9.5, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>треков</div>
            </div>
            <div>
              <div style={{ fontFamily: font.heading, fontSize: 22, color: C.silverHi, fontWeight: 400 }}>{taste.distinct_artists}</div>
              <div style={{ fontFamily: font.mono, fontSize: 9.5, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>артистов</div>
            </div>
            {formatDuration(taste.avg_duration_ms) && (
              <div>
                <div style={{ fontFamily: font.heading, fontSize: 22, color: C.silverHi, fontWeight: 400 }}>{formatDuration(taste.avg_duration_ms)}</div>
                <div style={{ fontFamily: font.mono, fontSize: 9.5, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.06em' }}>средняя длина</div>
              </div>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 24 }}>
            <div>
              <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginBottom: 10 }}>
                Жанры{taste.genre_coverage_pct < 99 ? ` · известны у ${taste.genre_coverage_pct}% треков` : ''}
              </div>
              {taste.genres.length > 0 ? (
                taste.genres.map((g, i) => (
                  <RankBar key={i} label={g.genre} value={g.pct} maxValue={taste.genres[0].pct} display={`${g.pct}%`} />
                ))
              ) : (
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted }}>
                  Жанры недоступны для этих треков (обычно у Spotify-плейлистов — источник не отдаёт жанр)
                </div>
              )}
            </div>
            <div>
              <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginBottom: 10 }}>Топ артистов</div>
              {taste.top_artists.map((a, i) => (
                <RankBar key={i} label={a.artist} value={a.count} maxValue={taste.top_artists[0].count} display={`${a.count}×`} />
              ))}
            </div>
          </div>
        </div>
      )}

      {playlists.length > 0 && (
        <div style={glass}>
          <div style={sectionLabel}>Плейлисты ({playlists.length})</div>
          {playlists.map(pl => <PlaylistCard key={pl.id} pl={pl} onDelete={handleDeletePlaylist} isOwner={isOwner} />)}
        </div>
      )}

      {playlists.length === 0 && (
        <div style={{ textAlign: 'center', padding: '30px 20px' }}>
          <p style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted }}>
            {isOwner ? 'Добавьте плейлист, чтобы начать.' : 'Плейлисты ещё не добавлены.'}
          </p>
        </div>
      )}

      {playlists.length > 0 && isOwner && (
        <div style={glass}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
            <div style={sectionLabel}>Слепое чтение личности по плейлисту</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: font.mono, fontSize: 10, color: C.textDim }}>
                треков в анализ
                <input
                  type="number"
                  min={1}
                  value={sampleSize}
                  onChange={e => setSampleSize(Math.max(1, parseInt(e.target.value) || 1))}
                  style={{ ...selectStyle, width: 72, padding: '5px 8px' }}
                />
              </label>
              <ModelToggle prefKey="music" />
              <button onClick={runAnalyze} disabled={analyzing} style={btnPrimary}>
                {analyzing ? '⏳ Анализирую...' : musicData ? '↻ Пересчитать' : '⬡ Прочитать'}
              </button>
            </div>
          </div>
          {musicData?.track_total_stored > 0 && (
            <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginBottom: 14, marginTop: -8 }}>
              В анализе {musicData.track_sample_size} из {musicData.track_total_stored} сохранённых треков (равномерная выборка)
            </div>
          )}

          {musicData && (
            <div>
              {musicData.genre_profile?.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
                  {musicData.genre_profile.map((g, i) => (
                    <span key={i} style={{
                      fontFamily: font.mono, fontSize: 10, padding: '3px 9px', borderRadius: 20,
                      border: `1px solid ${C.border}`, color: C.textMuted,
                    }}>{g}</span>
                  ))}
                </div>
              )}
              {musicData.mood_profile && (
                <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.55, marginBottom: 14, fontStyle: 'italic' }}>
                  {musicData.mood_profile}
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: font.heading, fontSize: 22, fontWeight: 400, color: C.silverHi }}>{musicData.archetype}</span>
                {musicData.mbti && <span style={{ fontFamily: font.mono, fontSize: 13, color: C.textDim }}>{musicData.mbti}</span>}
                {typeof musicData.overall_match_percent === 'number' && (
                  <span style={{ fontFamily: font.mono, fontSize: 10, color: C.accent }}>{musicData.overall_match_percent}% совпадение с профилем</span>
                )}
              </div>
              <MatchBadge match={musicData.archetype_match?.match} note={musicData.archetype_match?.match_note} />
              <div style={{ height: 6 }} />
              <MatchBadge match={musicData.mbti_match?.match} note={musicData.mbti_match?.match_note} />

              {musicData.summary && (
                <div style={{ marginTop: 14, fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.6 }}>
                  {musicData.summary}
                </div>
              )}

              {(musicData.traits || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={sectionLabel}>Черты</div>
                  {(musicData.traits || []).map((t, i) => {
                    const isObj = typeof t === 'object'
                    return (
                      <div key={i} style={{
                        padding: '8px 12px', borderRadius: 6, marginBottom: 6,
                        background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
                      }}>
                        <span style={{ fontFamily: font.body, fontSize: 12, color: C.text }}>{isObj ? t.text : t}</span>
                        {isObj && <MatchBadge match={t.match} note={t.match_note} />}
                      </div>
                    )
                  })}
                </div>
              )}

              {(musicData.core_values || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <div style={sectionLabel}>Ценности</div>
                  {(musicData.core_values || []).map((v, i) => {
                    const isObj = typeof v === 'object'
                    return (
                      <div key={i} style={{
                        padding: '8px 12px', borderRadius: 6, marginBottom: 6,
                        background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
                      }}>
                        <span style={{ fontFamily: font.body, fontSize: 12, color: C.text }}>{isObj ? v.text : v}</span>
                        {isObj && <MatchBadge match={v.match} note={v.match_note} />}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {playlists.length > 0 && otherProfiles.length > 0 && isOwner && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
          <div style={glass}>
            <div style={sectionLabel}>Совместимость вкуса</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <select value={compatTarget} onChange={e => setCompatTarget(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
                <option value="">Выберите профиль…</option>
                {otherProfiles.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <button onClick={runCompatibility} disabled={!compatTarget || compatLoading} style={btnGhost}>
                {compatLoading ? '⏳...' : 'Сравнить'}
              </button>
            </div>
            {compatError && <div style={{ fontFamily: font.body, fontSize: 12, color: 'rgba(220,100,100,0.9)' }}>{compatError}</div>}
            {compatResult && (
              <div>
                <div style={{ marginBottom: 10 }}><ScoreBar score={compatResult.overall_match_percent} /></div>
                <div style={{ fontFamily: font.mono, fontSize: 10.5, color: C.textDim, marginBottom: 10 }}>
                  По артистам: {compatResult.artist_match_percent}% · По жанрам: {compatResult.genre_match_percent}%
                </div>
                {compatResult.narrative && (
                  <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, lineHeight: 1.55, marginBottom: 10 }}>
                    {compatResult.narrative}
                  </div>
                )}
                {compatResult.shared_artists?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {compatResult.shared_artists.map((a, i) => (
                      <span key={i} style={{
                        fontFamily: font.mono, fontSize: 10, padding: '2px 9px', borderRadius: 20,
                        background: 'rgba(77,200,138,0.08)', border: '1px solid rgba(77,200,138,0.25)', color: 'rgba(77,200,138,0.9)',
                      }}>{a}</span>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={glass}>
            <div style={sectionLabel}>Что из моего плейлиста подойдёт</div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <select value={crossTarget} onChange={e => setCrossTarget(e.target.value)} style={{ ...selectStyle, flex: 1 }}>
                <option value="">Выберите профиль…</option>
                {otherProfiles.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
              </select>
              <button onClick={runCrossRecommend} disabled={!crossTarget || crossLoading} style={btnGhost}>
                {crossLoading ? '⏳...' : 'Подобрать'}
              </button>
            </div>
            {crossError && <div style={{ fontFamily: font.body, fontSize: 12, color: 'rgba(220,100,100,0.9)' }}>{crossError}</div>}
            {crossResult && (
              <div>
                {crossResult.recommendations?.length === 0 && (
                  <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted }}>
                    Не нашлось треков — либо у выбранного профиля нет плейлистов, либо всё уже совпадает.
                  </div>
                )}
                {(crossResult.recommendations || []).map((r, i) => (
                  <div key={i} style={{
                    padding: '8px 12px', borderRadius: 6, marginBottom: 6,
                    background: 'rgba(255,255,255,0.02)', border: `1px solid ${C.border}`,
                  }}>
                    <div style={{ fontFamily: font.body, fontSize: 12, color: C.text }}>{r.title} — {r.artist}</div>
                    {r.reason && <div style={{ fontFamily: font.body, fontSize: 11, color: C.textMuted, marginTop: 3 }}>{r.reason}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
